import SPLIT_FEATURES from 'containers/SplitContext/features'

export const FULFILMENT_PICKING_FLAG =
  SPLIT_FEATURES.BACK_FULFILMENT_PICKING_DELIVERY_ORDERS

export const CONFIG_KEYS = {
  PICKING_DELIVERY_LIST: 'picking_delivery_list',
  NEW_DASHBOARD: 'new_dashboard',
  BARCODE: 'barcode',
  CAPACITY_PLANNING: 'capacity_planning',
  TIMESLOT_BLOCKING: 'timeslot_blocking',
  BULK_OPERATIONS: 'bulk_operations',
  ZONE_PICKING_QUEUE: 'zone_picking_queue',
}

/** Config key → operations nav slug (items gated when master flag is on). */
export const NAV_CONFIG_TO_SLUG = {
  [CONFIG_KEYS.PICKING_DELIVERY_LIST]: 'picking-delivery-orders',
  [CONFIG_KEYS.CAPACITY_PLANNING]: 'capacity-planning',
  [CONFIG_KEYS.TIMESLOT_BLOCKING]: 'timeslot-blocking',
  [CONFIG_KEYS.BULK_OPERATIONS]: 'bulk-operations',
  [CONFIG_KEYS.ZONE_PICKING_QUEUE]: 'zone-picking-queue',
}

const TRUTHY = new Set(['true', '1', 'enabled', 'on', 'yes'])

/** When master is on and a key is omitted, preserve prior rollout behaviour. */
const DEFAULTS_WHEN_MASTER_ON = {
  [CONFIG_KEYS.PICKING_DELIVERY_LIST]: true,
  [CONFIG_KEYS.NEW_DASHBOARD]: true,
  [CONFIG_KEYS.BARCODE]: true,
  [CONFIG_KEYS.ZONE_PICKING_QUEUE]: true,
  [CONFIG_KEYS.CAPACITY_PLANNING]: false,
  [CONFIG_KEYS.TIMESLOT_BLOCKING]: false,
  [CONFIG_KEYS.BULK_OPERATIONS]: false,
}

function parseConfigRaw(splits) {
  const raw = splits?.[FULFILMENT_PICKING_FLAG]?.config
  if (raw == null || raw === '') {
    return {}
  }
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return {}
  }
}

export function isTruthyConfigValue(value) {
  if (value === true || value === 1) {
    return true
  }
  if (value == null || value === false) {
    return false
  }
  return TRUTHY.has(String(value).trim().toLowerCase())
}

export function isFulfilmentPickingMasterOn(splits) {
  return splits?.[FULFILMENT_PICKING_FLAG]?.treatment === 'on'
}

/**
 * When the master flag is off, picking-essentials features are disabled.
 * When on, each config key must be explicitly truthy to enable a feature.
 */
export function isFulfilmentPickingFeatureEnabled(splits, configKey) {
  if (!isFulfilmentPickingMasterOn(splits)) {
    return false
  }
  const config = parseConfigRaw(splits)
  if (!(configKey in config)) {
    return DEFAULTS_WHEN_MASTER_ON[configKey] === true
  }
  return isTruthyConfigValue(config[configKey])
}

/** Picking Delivery Orders list nav, routes, and batch deep-links. */
export function isPickingDeliveryListEnabled(splits) {
  return isFulfilmentPickingFeatureEnabled(
    splits,
    CONFIG_KEYS.PICKING_DELIVERY_LIST
  )
}

export function isNewDashboardEnabled(splits) {
  return isFulfilmentPickingFeatureEnabled(splits, CONFIG_KEYS.NEW_DASHBOARD)
}

export function isBarcodeToggleEnabled(splits) {
  return isFulfilmentPickingFeatureEnabled(splits, CONFIG_KEYS.BARCODE)
}

export function isZonePickingQueueNavEnabled(splits) {
  if (!isFulfilmentPickingMasterOn(splits)) {
    return true
  }
  return isFulfilmentPickingFeatureEnabled(
    splits,
    CONFIG_KEYS.ZONE_PICKING_QUEUE
  )
}

/**
 * When master is on, optional nav slugs are shown only if their config key is
 * truthy. When master is off, optional slugs are not filtered by this flag.
 */
export function shouldHideOperationsNavSlug(splits, slug) {
  if (slug === NAV_CONFIG_TO_SLUG[CONFIG_KEYS.PICKING_DELIVERY_LIST]) {
    const flag = splits?.[FULFILMENT_PICKING_FLAG]
    if (!flag) {
      return false
    }
    if (flag.treatment === 'off') {
      return true
    }
    return !isPickingDeliveryListEnabled(splits)
  }

  if (!isFulfilmentPickingMasterOn(splits)) {
    return false
  }

  const configKey = Object.entries(NAV_CONFIG_TO_SLUG).find(
    ([, navSlug]) => navSlug === slug
  )?.[0]

  if (!configKey || configKey === CONFIG_KEYS.PICKING_DELIVERY_LIST) {
    return false
  }

  return !isFulfilmentPickingFeatureEnabled(splits, configKey)
}

export function isOperationsNavSlugEnabled(splits, slug) {
  return !shouldHideOperationsNavSlug(splits, slug)
}

export const PICKING_DELIVERY_ORDERS_PATH =
  '/operations/picking-delivery-orders'

export function buildPickingDeliveryListLink(picklistId) {
  return `${PICKING_DELIVERY_ORDERS_PATH}?picklistId=${picklistId}&offset=0&page=1`
}

export function buildPickingDeliveryOrderDetailLink(referenceNumber) {
  return `${PICKING_DELIVERY_ORDERS_PATH}/${referenceNumber}`
}

export const DELIVERY_ORDERS_PATH = '/operations/delivery-orders'

export function buildDeliveryOrderDetailLink(referenceNumber) {
  return `${DELIVERY_ORDERS_PATH}/${referenceNumber}`
}

export function isPickingDeliveryOrdersContext(pathname = '') {
  return String(pathname).includes(PICKING_DELIVERY_ORDERS_PATH)
}

export function getDeliveryOrdersBasePath(pathname = '') {
  return isPickingDeliveryOrdersContext(pathname)
    ? PICKING_DELIVERY_ORDERS_PATH
    : DELIVERY_ORDERS_PATH
}

export function buildDeliveryOrderDetailLinkFromContext({
  referenceNumber,
  orderId,
  pathname,
} = {}) {
  const detailKey = referenceNumber || orderId
  const basePath = getDeliveryOrdersBasePath(pathname)
  return detailKey ? `${basePath}/${detailKey}` : basePath
}

function getNestedDeliveryOrdersBasePath({
  pathname,
  usePickingDeliveryOrdersPaths = false,
} = {}) {
  return usePickingDeliveryOrdersPaths ||
    isPickingDeliveryOrdersContext(pathname)
    ? PICKING_DELIVERY_ORDERS_PATH
    : DELIVERY_ORDERS_PATH
}

export function buildNestedDeliveryOrderLink({
  segment,
  id,
  pathname,
  usePickingDeliveryOrdersPaths = false,
} = {}) {
  if (!segment || id == null || id === '') {
    return null
  }

  const basePath = getNestedDeliveryOrdersBasePath({
    pathname,
    usePickingDeliveryOrdersPaths,
  })
  return `${basePath}/${segment}/${id}`
}

export function buildOrderLogLink({
  referenceNumber,
  id,
  pathname,
  usePickingDeliveryOrdersPaths = false,
} = {}) {
  return buildNestedDeliveryOrderLink({
    segment: 'order-log',
    id: referenceNumber ?? id,
    pathname,
    usePickingDeliveryOrdersPaths,
  })
}

export function buildEditPackedLink({
  referenceNumber,
  id,
  pathname,
  usePickingDeliveryOrdersPaths = false,
} = {}) {
  return buildNestedDeliveryOrderLink({
    segment: 'edit-packed',
    id: referenceNumber ?? id,
    pathname,
    usePickingDeliveryOrdersPaths,
  })
}

export function buildFileForReturnLink({
  referenceNumber,
  id,
  pathname,
  usePickingDeliveryOrdersPaths = false,
} = {}) {
  return buildNestedDeliveryOrderLink({
    segment: 'file-for-return',
    id: referenceNumber ?? id,
    pathname,
    usePickingDeliveryOrdersPaths,
  })
}

export function buildPickingLogLink({
  orderId,
  referenceNumber,
  pathname,
  usePickingDeliveryOrdersPaths = false,
} = {}) {
  if (!orderId) {
    return null
  }

  const basePath = getNestedDeliveryOrdersBasePath({
    pathname,
    usePickingDeliveryOrdersPaths,
  })
  const refQuery = referenceNumber
    ? `?ref=${encodeURIComponent(referenceNumber)}`
    : ''

  return `${basePath}/picking-log/${orderId}${refQuery}`
}

/** Zone picking queue and similar surfaces: use picking detail route when enabled. */
export function buildZonePickingQueueOrderDetailLink(referenceNumber, splits) {
  if (canLinkToPickingDeliveryList(splits)) {
    return buildPickingDeliveryOrderDetailLink(referenceNumber)
  }
  return buildDeliveryOrderDetailLink(referenceNumber)
}

export function canLinkToPickingDeliveryList(splits) {
  return isPickingDeliveryListEnabled(splits)
}

/** Pre-rollout Batch deep-link: the delivery orders list filtered by picklist. */
export function buildDeliveryOrderListByPicklistLink(picklistId) {
  return `${DELIVERY_ORDERS_PATH}?picklistId=${picklistId}&offset=0&page=1`
}

/**
 * Batch deep-link target. With the flag off the pre-rollout link is kept, so
 * turning the flag off leaves the surfaces that show a Batch number unchanged.
 */
export function buildBatchLink(picklistId, splits) {
  return canLinkToPickingDeliveryList(splits)
    ? buildPickingDeliveryListLink(picklistId)
    : buildDeliveryOrderListByPicklistLink(picklistId)
}
